import React, { forwardRef, useContext } from 'react'
import styled, { css, ThemeContext } from 'styled-components'

import { fastTransition } from '../../styles/effects'
import { applyTextStyle } from '../../styles/typography'
import { Icon } from '../icon'
import { IconType } from '../icon/IconType'

export interface IProps {
  icon?: IconType
  label: string
  count?: number
  active?: boolean
  disabled?: boolean
  isOpen?: boolean
  onClick?: () => void
  className?: string
  id?: string
  ariaControls?: string
}

const FilterTrigger = forwardRef<HTMLButtonElement, IProps>(
  (
    {
      icon,
      label,
      count,
      active,
      disabled,
      isOpen,
      onClick,
      className,
      id,
      ariaControls,
    },
    ref
  ) => {
    const theme = useContext(ThemeContext)
    const showCount = count !== undefined && count > 0
    const isActive = active || showCount
    const isPressed = !!isOpen
    const isFilled = isActive || isPressed

    const iconColor = disabled
      ? theme.colors.textLighter
      : theme.colors.lightest

    return (
      <Button
        ref={ref}
        type="button"
        id={id}
        className={className}
        onClick={onClick}
        disabled={disabled}
        active={isActive}
        pressed={isPressed}
        aria-haspopup="dialog"
        aria-expanded={isOpen ?? false}
        aria-controls={ariaControls}
      >
        {icon && (
          <IconSlot filled={isFilled} disabled={disabled}>
            <Icon type={icon} size={16} color={iconColor} />
          </IconSlot>
        )}
        <Label active={isActive} pressed={isPressed} disabled={disabled}>
          {label}
        </Label>
        {showCount && <CountBadge>{count}</CountBadge>}
      </Button>
    )
  }
)

FilterTrigger.displayName = 'FilterTrigger'

const Button = styled.button<{ active?: boolean; pressed?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  background-color: transparent;
  transition: background-color ${fastTransition};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.colors.primaryFadeLight};
  }

  &:disabled {
    cursor: default;
  }

  ${({ pressed, theme }) =>
    pressed &&
    css`
      background-color: ${theme.colors.primaryFadeLight};
    `}

  ${({ active, theme }) =>
    active &&
    css`
      background-color: ${theme.colors.primaryFadeLight};

      &:hover:not(:disabled) {
        background-image: linear-gradient(
          ${theme.colors.primaryFadeLight},
          ${theme.colors.primaryFadeLight}
        );
      }
    `}
`

const IconSlot = styled.span<{ filled?: boolean; disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 4px;
  background-color: ${({ theme, filled, disabled }) =>
    disabled
      ? theme.colors.primaryFadeLight
      : filled
      ? theme.colors.primary
      : theme.colors.primaryFadeHalf};
  transition: background-color ${fastTransition};

  & svg path {
    fill: ${({ theme, disabled }) =>
      disabled ? theme.colors.textLighter : theme.colors.lightest};
  }
`

const Label = styled.span<{
  active?: boolean
  pressed?: boolean
  disabled?: boolean
}>`
  ${applyTextStyle('labelHeavy')}
  color: ${({ theme, active, pressed, disabled }) => {
    if (disabled) return theme.colors.textLighter
    if (pressed && !active) return theme.colors.primary
    if (active) return theme.colors.textDark
    return theme.colors.text
  }};
  transition: color ${fastTransition};
`

const CountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  padding: 2px 4px;
  border-radius: 4px;
  background-color: ${({ theme }) => theme.colors.lightest};
  background-image: linear-gradient(
    ${({ theme }) => theme.colors.primaryFadeLight},
    ${({ theme }) => theme.colors.primaryFadeLight}
  );
  color: ${({ theme }) => theme.colors.primary};
  ${applyTextStyle('label')}
`

export default FilterTrigger
